import type { PostV1WorkspacesWorkspaceidEventConnectionsMutationRequest } from '@emma/api_client/types';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { useApiFetch, useApiMutation } from '../api';

export function useWorkspaceEventConnections(workspaceId: number) {
  return useApiFetch(
    `workspaces/${workspaceId}/event-connections`,
    async (callApi) =>
      await callApi((api) =>
        api.workspaces.getV1WorkspacesWorkspaceidEventConnections({ workspaceId })
      )
  );
}

export function useAddWorkspaceEventConnection(
  workspaceId: number,
  actionOnErr: () => void = () => {}
) {
  const { mutate } = useSWRConfig();
  const listCacheKey = `workspaces/${workspaceId}/event-connections`;

  const addMutation = useApiMutation(
    listCacheKey,
    async (callApi, data: PostV1WorkspacesWorkspaceidEventConnectionsMutationRequest) => {
      const response = await callApi((api) =>
        api.workspaces.postV1WorkspacesWorkspaceidEventConnections({ workspaceId }, data)
      );
      return response.data;
    },
    actionOnErr
  );

  return useCallback(
    async (data: PostV1WorkspacesWorkspaceidEventConnectionsMutationRequest) => {
      const result = await addMutation(data);
      await mutate((key: unknown) => typeof key === 'string' && key.startsWith(listCacheKey));
      return result;
    },
    [addMutation, mutate, listCacheKey]
  );
}

export function useDeleteWorkspaceEventConnection(
  workspaceId: number,
  actionOnErr: () => void = () => {}
) {
  const { mutate } = useSWRConfig();
  const listCacheKey = `workspaces/${workspaceId}/event-connections`;

  const deleteMutation = useApiMutation(
    `${listCacheKey}/delete`,
    async (callApi, connectionId: string) => {
      await callApi((api) =>
        api.workspaces.deleteV1WorkspacesWorkspaceidEventConnectionsConnectionid({
          workspaceId,
          connectionId,
        })
      );
      return connectionId;
    },
    actionOnErr
  );

  return useCallback(
    async (connectionId: string) => {
      await deleteMutation(connectionId);
      await mutate((key: unknown) => typeof key === 'string' && key.startsWith(listCacheKey));
      return true;
    },
    [deleteMutation, mutate, listCacheKey]
  );
}
